import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Download, 
  Printer, 
  Search, 
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Filter,
  Trash2
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

function MaintenanceHistory({ user }) {
  const { t } = useLanguage();
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [frequencyFilter, setFrequencyFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Load maintenance task history from API
  useEffect(() => {
    const loadMaintenanceHistory = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API}/maintenance-history`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const history = await response.json();
          console.log('Loaded maintenance history:', history.length);
          
          // Transform data to match UI expectations
          const transformedHistory = history.map(item => ({
            id: item.id,
            taskId: item.work_order_id,
            taskTitle: item.title,
            checklistItem: 'Task Completed',
            department: item.department_name || 'N/A',
            machine: item.machine_name || 'N/A',
            frequency: item.frequency || 'N/A',
            priority: item.priority || 'Medium',
            status: 'Completed',
            completedBy: item.completed_by_name || 'Unknown',
            completedAt: item.completed_at,
            notes: item.notes || '',
            type: 'maintenance',
            scheduled_date: item.scheduled_date
          }));
          
          setMaintenanceHistory(transformedHistory);
          setFilteredHistory(transformedHistory);
        } else {
          console.error('Failed to fetch maintenance history');
          toast({
            title: "Error",
            description: "Failed to load maintenance history",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error loading maintenance history:', error);
        toast({
          title: "Error",
          description: "Failed to load maintenance history",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadMaintenanceHistory();
    
    // Listen for task completion events
    const handleTaskCompleted = () => {
      console.log('Task completed, reloading maintenance history');
      loadMaintenanceHistory();
    };
    
    window.addEventListener('taskCompleted', handleTaskCompleted);
    
    return () => {
      window.removeEventListener('taskCompleted', handleTaskCompleted);
    };
  }, [API, toast]);

  // Filter history based on search and filters
  useEffect(() => {
    let filtered = maintenanceHistory;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.taskTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.checklistItem.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.machine.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.completedBy.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status.toLowerCase() === statusFilter);
    }

    // Frequency filter
    if (frequencyFilter !== 'all') {
      filtered = filtered.filter(item => item.frequency.toLowerCase() === frequencyFilter);
    }

    setFilteredHistory(filtered);
  }, [maintenanceHistory, searchTerm, statusFilter, frequencyFilter]);

  const handleDownload = () => {
    try {
      const csvContent = [
        [t('date'), t('taskTitle'), t('checklistItem'), t('department'), t('machine'), t('frequency'), t('priority'), t('status'), t('completedBy'), t('notes')],
        ...filteredHistory.map(item => [
          new Date(item.completedAt).toLocaleDateString(),
          item.taskTitle,
          item.checklistItem,
          item.department,
          item.machine,
          item.frequency,
          item.priority,
          item.status,
          item.completedBy,
          item.notes.replace(/,/g, ';') // Replace commas in notes
        ])
      ];

      const csvString = csvContent.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `maintenance-history-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: t('maintenanceHistoryDownloaded'),
        variant: "default"
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: t('failedToDownloadHistory'),
        variant: "destructive"
      });
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t('maintenanceHistoryReport')}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; border-bottom: 2px solid #ddd; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .header { margin-bottom: 20px; }
            .summary { background: #f0f9ff; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${t('maintenanceHistoryReport')}</h1>
            <p><strong>${t('generated')}:</strong> ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
            <p><strong>${t('user')}:</strong> ${user.full_name || user.username} (${user.role})</p>
          </div>
          
          <div class="summary">
            <h3>${t('summary')}</h3>
            <p><strong>${t('totalRecordsLabel')}:</strong> ${filteredHistory.length}</p>
            <p><strong>${t('completedTasksLabel')}:</strong> ${filteredHistory.filter(h => h.status === 'Completed').length}</p>
            <p><strong>${t('dateRangeLabel')}:</strong> ${filteredHistory.length > 0 ? 
              `${new Date(Math.min(...filteredHistory.map(h => new Date(h.completedAt)))).toLocaleDateString()} - ${new Date(Math.max(...filteredHistory.map(h => new Date(h.completedAt)))).toLocaleDateString()}` : t('noData')}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>${t('date')}</th>
                <th>${t('taskTitle')}</th>
                <th>${t('department')}</th>
                <th>${t('machine')}</th>
                <th>${t('frequency')}</th>
                <th>${t('completedBy')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredHistory.map(item => `
                <tr>
                  <td>${new Date(item.completedAt).toLocaleDateString()}</td>
                  <td>${item.taskTitle}</td>
                  <td>${item.department}</td>
                  <td>${item.machine}</td>
                  <td>${item.frequency}</td>
                  <td>${item.completedBy}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleDeleteHistoryItem = async (item) => {
    if (!window.confirm(`Are you sure you want to delete this history entry?\n\n"${item.taskTitle}"\n\nThis action cannot be undone.`)) {
      return;
    }
    try {
      const response = await fetch(`${API}/maintenance-history/${item.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        setMaintenanceHistory(prev => prev.filter(h => h.id !== item.id));
        toast({ title: "History entry deleted", variant: "default" });
      } else {
        toast({ title: "Failed to delete history entry", variant: "destructive" });
      }
    } catch (error) {
      console.error('Error deleting history item:', error);
      toast({ title: "Failed to delete history entry", variant: "destructive" });
    }
  };

  const handlePrintHistoryItem = (item) => {
    // Fetch the full history record with all details
    fetch(`${API}/maintenance-history/${item.id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
    .then(response => response.json())
    .then(historyData => {
      const printWindow = window.open('', '_blank');
      
      const checklistHtml = historyData.checklist && historyData.checklist.length > 0
        ? historyData.checklist.map(item => `
            <div class="checklist-item">
              <span class="checkbox">${item.completed ? '☑' : '☐'}</span>
              <span class="checklist-text">${item.text}</span>
            </div>
          `).join('')
        : '<p>No checklist items defined.</p>';

      const completedCount = historyData.checklist ? historyData.checklist.filter(item => item.completed).length : 0;
      const totalCount = historyData.checklist ? historyData.checklist.length : 0;

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
              @page { margin: 0.5cm; }
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              border-bottom: 3px solid #333;
              padding-bottom: 10px;
            }
            .date {
              color: #666;
              font-size: 12px;
            }
            h1 {
              font-size: 24px;
              margin: 5px 0;
              color: #1a1a1a;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
              margin: 10px 0;
              background: #f8f9fa;
              padding: 15px;
              border-radius: 8px;
            }
            .info-item {
              display: flex;
              gap: 10px;
            }
            .info-label {
              font-weight: bold;
              color: #495057;
              min-width: 120px;
            }
            .section {
              margin: 10px 0 0 0;
            }
            .section-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 8px;
              color: #1a1a1a;
              border-bottom: 2px solid #dee2e6;
              padding-bottom: 5px;
            }
            .checklist-item {
              padding: 5px;
              border-bottom: 1px solid #e9ecef;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .checkbox {
              font-size: 18px;
            }
            .checklist-text {
              flex: 1;
            }
            .notes-box {
              padding: 10px 0;
              min-height: 30px;
              white-space: pre-wrap;
            }
            .signature-section {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              margin-top: 30px;
            }
            .signature-box {
              border-top: 2px solid #333;
              padding-top: 10px;
              text-align: center;
              font-weight: bold;
            }
            .badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 600;
            }
            .badge-completed { background: #d4edda; color: #155724; }
            .print-button {
              padding: 10px 20px;
              background: #007bff;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
            }
            .print-button:hover {
              background: #0056b3;
            }
          </style>
        </head>
        <body>
          <div class="no-print" style="margin-bottom: 20px;">
            <button class="print-button" onclick="window.print()">🖨️ Print</button>
          </div>
          
          <div class="header">
            <div>
              <div class="date">${new Date(historyData.completed_at).toLocaleString()}</div>
              <h1>Maintenance Work Order</h1>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Task Information</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Task:</span>
                <span>${historyData.title}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Department:</span>
                <span>${historyData.department_name || 'N/A'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Date:</span>
                <span>${new Date(historyData.scheduled_date).toLocaleDateString()}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Machine:</span>
                <span>${historyData.machine_name || 'N/A'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Assignees:</span>
                <span>${historyData.assignee_names && historyData.assignee_names.length > 0 ? historyData.assignee_names.join(', ') : 'Unassigned'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Frequency:</span>
                <span>${historyData.frequency || 'N/A'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Priority:</span>
                <span>${historyData.priority}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Scheduled Time:</span>
                <span>${historyData.scheduled_time || 'N/A'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Status:</span>
                <span class="badge badge-completed">${historyData.status || 'Completed'}</span>
              </div>
            </div>
          </div>

          ${historyData.description ? `
          <div class="section">
            <div class="section-title">Instructions</div>
            <div class="notes-box">${historyData.description}</div>
          </div>
          ` : ''}

          ${historyData.safety_notes ? `
          <div class="section">
            <div class="section-title">Safety Notes</div>
            <div class="notes-box">${historyData.safety_notes}</div>
          </div>
          ` : ''}

          <div class="section">
            <div class="section-title">Checklist (${completedCount}/${totalCount} completed)</div>
            ${checklistHtml}
          </div>

          ${historyData.notes ? `
          <div class="section">
            <div class="section-title">Notes/Comments</div>
            <div class="notes-box">${historyData.notes}</div>
          </div>
          ` : ''}

          <div class="signature-section">
            <div class="signature-box">Technician Signature</div>
            <div class="signature-box">Supervisor Signature</div>
          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
    })
    .catch(error => {
      console.error('Error fetching history details:', error);
      toast({
        title: "Error",
        description: "Failed to load print data",
        variant: "destructive"
      });
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loadingMaintenanceHistory')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('maintenanceHistory')}</h1>
          <p className="text-gray-600 mt-1">{t('viewHistoryMaintenance')}</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={handleDownload}
            className="bg-green-600 hover:bg-green-700 text-white"
            disabled={filteredHistory.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            {t('downloadCSV')}
          </Button>
          <Button
            onClick={handlePrint}
            variant="outline"
            disabled={filteredHistory.length === 0}
          >
            <Printer className="w-4 h-4 mr-2" />
            {t('print')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('filters')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder={t('searchWorkOrders')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('status')}</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">{t('allStatuses')}</option>
                <option value="completed">{t('completed')}</option>
                <option value="pending">{t('pending')}</option>
                <option value="overdue">{t('overdue')}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('frequency')}</label>
              <select
                value={frequencyFilter}
                onChange={(e) => setFrequencyFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">{t('allFrequencies')}</option>
                <option value="daily">{t('daily')}</option>
                <option value="weekly">{t('weekly')}</option>
                <option value="monthly">{t('monthly')}</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setFrequencyFilter('all');
                }}
                variant="outline"
                className="w-full"
              >
                <Filter className="w-4 h-4 mr-2" />
                {t('clearFilters')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{filteredHistory.filter(h => h.status === 'Completed').length}</p>
                <p className="text-gray-600">{t('completedTasks')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{filteredHistory.length}</p>
                <p className="text-gray-600">{t('totalRecords')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">
                  {filteredHistory.length > 0 ? Math.ceil((new Date() - new Date(Math.min(...filteredHistory.map(h => new Date(h.completedAt))))) / (1000 * 60 * 60 * 24)) : 0}
                </p>
                <p className="text-gray-600">{t('daysOfHistory')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('maintenanceTaskHistory')}</CardTitle>
          <p className="text-sm text-gray-600">
            {filteredHistory.length} {t('recordsOf')} {maintenanceHistory.length} {t('totalRecords').toLowerCase()}
          </p>
        </CardHeader>
        <CardContent>
          {filteredHistory.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noMaintenanceHistoryFound')}</h3>
              <p className="text-gray-600">
                {maintenanceHistory.length === 0 
                  ? t('completeSomeTasks')
                  : t('tryAdjustingFilters')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">{t('date')}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">{t('taskTitle')}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">{t('item')}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">{t('department')}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">{t('machine')}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Frequency</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">{t('assignee')}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((item, index) => (
                    <tr key={item.id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDate(item.completedAt)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{item.taskTitle}</div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {item.checklistItem}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {item.department}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {item.machine}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-xs">
                          {item.frequency}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge 
                          variant={item.status === 'Completed' ? 'default' : 'secondary'}
                          className={item.status === 'Completed' ? 'bg-green-100 text-green-800' : ''}
                        >
                          {item.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {item.completedBy}
                      </td>
                      <td className="py-3 px-4 flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePrintHistoryItem(item)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`print-maintenance-history-${item.id}`}
                        >
                          <Printer className="h-4 w-4 mr-1" />
                          Print
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteHistoryItem(item)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-maintenance-history-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {t('delete') || 'Delete'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default MaintenanceHistory;